<template>
  <div v-if="isOpen && showShort"
       class="route-details route-details--is-short"
  >
    <routes-list-item :route="route"
                      :is-interactive="false"
    />

    <button class="btn-default route-details__close-button route-details__close-button--is-short"
            @click="closeRoutes"
    >
      <app-icon icon="close"/>
    </button>

    <div class="route-details-controls">
      <button class="btn"
              @click="toggleMap"
      >
        <app-icon icon="arrow-left"/>

        {{ $t('routeDetails.back') }}
      </button>
    </div>
  </div>

  <div v-else-if="isOpen"
       class="route-details"
       :class="modifiers"
  >
    <div class="route-details__head">
      <button class="btn btn-icon btn--is-sm route-details__md-back-button"
              @click="closeRoute"
      >
        <app-icon icon="arrow-left"/>
      </button>

      <h3 class="route-details__title">
        {{ blockTitle }}
      </h3>

      <button class="btn-default route-details__close-button"
              @click="closeRoute"
      >
        <app-icon icon="close"/>
      </button>

      <div class="route-details__md-editing-controls">
        <button class="btn-default route-details__md-rollup"
                @click="isMdEditingRolledUp = !isMdEditingRolledUp"
        >
          <app-icon icon="arrow-right-double"/>
        </button>

        <button class="btn-default"
                @click="closeRoute"
        >
          <app-icon icon="close"/>
        </button>
      </div>
    </div>

    <div v-if="$store.state.Coordinates.coordinates"
         class="route-details__content"
    >
      <vue-draggable v-model="listModel"
                     :options="draggableOptions"
      >
        <RouteDetailsItem v-for="(routeDetailItem, index) in routeDetailItems"
                          :key="index"
                          :coordinate="routeDetailItem.coordinate"
                          :route="routeDetailItem.route"
                          :label="routeDetailItem.label"
                          :number="routeDetailItem.number"
        />
      </vue-draggable>

      <RouteDetailsItem v-if="isRouteEditing"
                        :label="$t('routeDetails.nextGoalLabel')"
                        :number="routeDetailItems.length + 1"
      />
    </div>

    <route-details-controls v-if="route"
                            :route="route"
    />

    <div class="route-details__md-footer">
      <button class="btn"
              @click="toggleMap"
      >
        <app-icon icon="map"/>

        {{ $t('routeDetails.showOnMap') }}
      </button>
    </div>
  </div>
</template>

<script>
import VueDraggable from "vuedraggable";
import RoutesListItem from "@/components/common/RoutesList/RoutesListItem.vue";
import RouteDetailsItem from "@/components/common/RouteDetails/RouteDetailsItem.vue";
import RouteDetailsControls from "@/components/common/RouteDetails/RouteDetailsControls.vue";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "RouteDetails",
  components: {
    VueDraggable,
    RoutesListItem,
    RouteDetailsItem,
    RouteDetailsControls
  },
  data() {
    return {
      isMdShort: true,
      isMdEditingRolledUp: true
    }
  },
  computed: {
    ...mapGetters('Routes', {
      'route': 'activeRoute'
    }),
    ...mapGetters('Interface', {
      'isOpen': 'isRouteDetailsOpen'
    }),

    isRouteNew() {
      return this.route?.isNew || false
    },
    isRouteEditing() {
      return this.route?.isEditing || false
    },
    blockTitle() {
      return this.isRouteNew ? this.$t('routeDetails.newRouteTitle')
        : this.route?.isCustom ? this.$t('routeDetails.customRouteTitle')
        : this.$t('routeDetails.predefinedRouteTitle')
    },
    coordinates() {
      return this.route?.coordinates || []
    },
    routeDetailItems() {
      return this.coordinates.map((coordinate, index) => {
        return {
          id: coordinate.id,
          coordinate,
          route: this.route,
          label: coordinate.street?.name,
          number: index + 1
        }
      })
    },
    listModel: {
      get() {
        return this.routeDetailItems
      },
      set(value) {
        const coordinatesIds = value.map(({ id }) => id)

        this.setRouteCoordinatesIds({
          routeId: this.route?.id,
          coordinatesIds
        })
      }
    },
    draggableOptions() {
      return {
        disabled: !this.route.isEditing,
        handle: '.route-details-item__drag-handle'
      }
    },
    showShort() {
      return this.$bp.isMdOrLess && this.isMdShort && !this.isRouteEditing
    },
    modifiers() {
      return {
        'route-details--is-editing': this.isRouteEditing,
        'route-details--is-rolled-up': this.isMdEditingRolledUp
      }
    },
     uniqueRouteDetailItems() {
      const map = new Map()
      this.routeDetailItems.forEach(item => {
        if (item && item.id && !map.has(item.id)) {
          map.set(item.id, item)
        }
      })
      return [...map.values()]
    }
  },
  watch: {
    isOpen() {
      this.isMdShort = false
    }
  },
  methods: {
    closeRoute() {
      this.setActiveRouteId(null)
    },
    closeRoutes() {
      this.setRoutesListOpenState(false)
      this.closeRoute()
    },
    toggleMap() {
      this.isMdShort = !this.isMdShort
    },

    ...mapActions('Interface', [
      'setRoutesListOpenState'
    ]),
    ...mapActions('Routes', [
      'setActiveRouteId',
      'setRouteCoordinatesIds'
    ])
  }
}
</script>

<style lang="scss">
.route-details {
  position: absolute;
  top: $content-padding-top;
  right: calc($timeline-width + 28px);
  bottom: $content-padding-bottom;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 370px;
  max-height: 410px;
  padding: 20px 20px 23px;
  background-color: $app-block-color;
  box-shadow: 0 4px 4px $app-box-shadow-color;
  z-index: 24;

  &--is-short {
    display: none;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 1px solid $app-text-color;
    padding-bottom: 16px;
    margin-bottom: 10px;
  }

  &__md-back-button {
    display: none !important;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
    text-transform: uppercase;
    margin-right: auto;
  }

  &__content {
    margin-bottom: auto;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &__md-footer,
  &__md-editing-controls {
    display: none;
  }

}

@include smallerThan("xl") {
  .route-details {
    right: 210px;
  }
}

@include smallerThan("lg") {
  .route-details {
    right: 0;
    bottom: $content-padding-bottom;
    max-height: 370px;
  }
}

@include smallerThan("md") {
  $offset-bottom: calc($content-padding-bottom + $mobile-navigator-item-size + 16px);

  .route-details {
    position: fixed;              
    bottom: calc($mobile-navigator-item-size + 10px); 
    top: 0;
    width: 100%;
    max-height: 530px;
    z-index: 9999;

    &--is-short,
    &--is-editing {
      display: block;
      top: unset;
      bottom: $offset-bottom;
      height: unset;
      padding: 15px 20px;

      .routes-list-item {
        padding: 0;
      }

    }

    &__md-back-button {
      display: flex !important;
      margin-right: 14px;
    }

    &__close-button {
      &:not(&--is-short) {
        display: none;
      }

      &--is-short {
        position: absolute;
        top: 15px;
        right: 20px;
      }
    }

    &__md-footer {
      display: block;
      margin-top: 20px;
    }

    &--is-editing &__content {
      max-height: 195px;
      transition: max-height .3s;
    }
    &--is-editing &__md-footer {
      display: none;
    }
    &--is-editing &__md-back-button {
      display: none !important;
    }
    &--is-editing &__md-editing-controls {
      display: flex;
      grid-column-gap: 20px;
    }
    &--is-editing#{&}--is-rolled-up &__content {
      max-height: 0;
    }
    &--is-editing#{&}--is-rolled-up .route-details-controls {
      display: none;
    }
    &__md-rollup {
      .app-icon {
        transition: .3s;
        transform: rotate(90deg);
      }
    }
    &--is-editing#{&}--is-rolled-up &__md-rollup .app-icon {
      transform: rotate(-90deg);
    }
    &--is-editing#{&}--is-rolled-up &__head {
      padding-bottom: 0;
      border-bottom: none;
      margin-bottom: 0;
    }
  }
}
</style>
